import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState, useCallback } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { DestructiveConfirm } from '@/components/ui/DestructiveConfirm'
import { BRAND_MINT } from '@/lib/utils'
import {
  getAdminRequestsApi,
  getAdminRequestByIdApi,
  cancelAdminRequestApi,
  type AdminRequest,
  type AdminRequestDetailResponse,
} from '@/lib/api'
import {
  LoaderIcon,
  SearchIcon,
  FilterIcon,
  XIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BanknoteIcon,
  ActivityIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  AlertTriangleIcon,
  EyeIcon,
  EyeOffIcon,
  UsersIcon,
  BuildingIcon,
  HashIcon,
  TagIcon,
  BanIcon,
  ArrowLeftIcon,
} from 'lucide-react'

const CIRCLE_STATUS_STYLES: Record<string, { label: string; color: string }> = {
  open: { label: 'Open', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  full: { label: 'Full', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  paying: { label: 'Paying', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  completed: { label: 'Completed', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  cancelled: { label: 'Cancelled', color: 'bg-muted text-muted-foreground' },
}

function formatTimestamp(iso: string) {
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function formatDate(iso: string) {
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export const Route = createFileRoute('/dashboard/admin/requests')({
  component: () => {
    const [requests, setRequests] = useState<AdminRequest[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [limit] = useState(50)
    const [loading, setLoading] = useState(true)

    // Filters
    const [statusFilter, setStatusFilter] = useState('')
    const [showFilters, setShowFilters] = useState(false)

    // Detail view
    const [detail, setDetail] = useState<AdminRequestDetailResponse | null>(null)
    const [detailLoading, setDetailLoading] = useState(false)
    const [showDetail, setShowDetail] = useState(false)

    // Cancel
    const [cancelling, setCancelling] = useState(false)
    const [confirmCancel, setConfirmCancel] = useState(false)

    const totalPages = Math.max(1, Math.ceil(total / limit))

    const buildParams = useCallback(() => {
      const params: Record<string, string | number> = { page, limit }
      if (statusFilter) params.status = statusFilter
      return params
    }, [page, limit, statusFilter])

    useEffect(() => {
      let cancelled = false
      async function load() {
        setLoading(true)
        try {
          const res = await getAdminRequestsApi(buildParams())
          if (!cancelled) { setRequests(res.data); setTotal(res.total) }
        } catch (err) {
          if (!cancelled) toast.error(err instanceof Error ? err.message : 'Failed to load requests')
        } finally { if (!cancelled) setLoading(false) }
      }
      load()
      return () => { cancelled = true }
    }, [buildParams])

    const clearFilters = () => { setStatusFilter(''); setPage(1) }
    const hasFilters = !!statusFilter

    const openDetail = async (requestId: number) => {
      setShowDetail(true)
      setDetailLoading(true)
      try {
        const res = await getAdminRequestByIdApi(requestId)
        setDetail(res)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to load request details')
        setShowDetail(false)
      } finally { setDetailLoading(false) }
    }

    const closeDetail = () => { setShowDetail(false); setDetail(null); setConfirmCancel(false) }

    const handleCancel = async () => {
      if (!detail) return
      setCancelling(true)
      setConfirmCancel(false)
      try {
        await cancelAdminRequestApi(detail.request.circle_req_id)
        setRequests((prev) => prev.map((r) => r.circle_req_id === detail.request.circle_req_id ? { ...r, status: 'cancelled' } : r))
        setDetail({ ...detail, request: { ...detail.request, status: 'cancelled' } })
        toast.success('Request cancelled successfully.')
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to cancel request')
      } finally { setCancelling(false) }
    }

    // Loading state
    if (loading && requests.length === 0) {
      return (
        <div className="mx-auto max-w-5xl">
          <div className="mb-8">
            <div className="flex items-start gap-3">
              <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
              <h1 className="text-2xl font-semibold">Circle oversight</h1>
            </div>
          </div>
          <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
            <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
            <p className="ml-2 text-sm text-muted-foreground">Loading Circles…</p>
          </div>
        </div>
      )
    }

    return (
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <div className="flex items-start gap-3">
            <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
            <div className="flex flex-1 items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold">Circle oversight</h1>
                <p className="mt-1 text-sm text-muted-foreground">View and manage all Funding Circles across the platform.</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="gap-1.5 shrink-0">
                <FilterIcon className="size-4" />{showFilters ? 'Hide filters' : 'Filters'}
                {hasFilters && <span className="flex size-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">!</span>}
              </Button>
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <div className="mb-6 space-y-3">
          {showFilters && (
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">Status</label>
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary">
                    <option value="">All statuses</option>
                    <option value="open">Open</option>
                    <option value="full">Full</option>
                    <option value="paying">Paying</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="flex items-end">
                  {hasFilters && <Button variant="outline" size="sm" onClick={clearFilters} className="gap-1.5"><XIcon className="size-4" />Clear</Button>}
                </div>
              </div>
            </div>
          )}
        </div>

        <p className="mb-4 text-xs text-muted-foreground">
          {total} Circle{total !== 1 ? 's' : ''}{page > 1 && ` — page ${page} of ${totalPages}`}
        </p>

        {requests.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
            <BanknoteIcon className="size-10 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground">No Circles found</p>
            <p className="text-sm text-muted-foreground">{hasFilters ? 'No Circles match the current filters.' : 'No Funding Circles have been created yet.'}</p>
            {hasFilters && <Button variant="outline" size="sm" onClick={clearFilters} className="mt-2 gap-1.5"><XIcon className="size-4" />Clear filters</Button>}
          </div>
        ) : (
          <div className="space-y-2">
            {requests.map((req) => {
              const statusInfo = CIRCLE_STATUS_STYLES[req.status] ?? { label: req.status, color: 'bg-muted text-muted-foreground' }
              return (
                <button key={req.circle_req_id} type="button" onClick={() => openDetail(req.circle_req_id)}
                  className="w-full rounded-xl border border-border bg-card p-4 text-left transition-shadow hover:shadow-sm"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <BanknoteIcon className="size-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-foreground">{req.title ?? `${req.creator_name}'s Circle`}</span>
                        <span className="ml-1 shrink-0 rounded-md bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">#{req.circle_req_id}</span>
                        <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${statusInfo.color}`}>
                          <ActivityIcon className="size-3" />
                          {statusInfo.label}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                          <UsersIcon className="size-3" />
                          {req.spots_progress}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <BuildingIcon className="size-3" />
                          {req.org_name || 'Unknown'}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <ClockIcon className="size-3" />
                          Created {formatDate(req.created_at)}
                        </span>
                      </div>
                    </div>
                    <EyeIcon className="mt-1 size-4 shrink-0 text-muted-foreground" />
                  </div>
                </button>
              )
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-3">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="gap-1.5"><ChevronLeftIcon className="size-4" />Previous</Button>
            <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="gap-1.5">Next<ChevronRightIcon className="size-4" /></Button>
          </div>
        )}

        {/* Detail Drawer */}
        {showDetail && (
          <>
            <button type="button" aria-label="Close detail" className="fixed inset-0 z-40 bg-black/30" onClick={closeDetail} />
            <aside aria-label="Circle details" className="fixed inset-y-0 right-0 z-50 w-full max-w-lg border-l border-border bg-background shadow-xl motion-safe:animate-in motion-safe:slide-in-from-right motion-safe:duration-200">
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <h2 className="text-base font-semibold text-foreground">Circle details</h2>
                <button type="button" onClick={closeDetail} className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" aria-label="Close">
                  <XIcon className="size-4" />
                </button>
              </div>
              <div className="overflow-y-auto p-5" style={{ height: 'calc(100vh - 65px)' }}>
                {detailLoading ? (
                  <div className="flex items-center justify-center py-16"><LoaderIcon className="size-5 animate-spin text-muted-foreground" /></div>
                ) : detail ? (
                  <div className="space-y-6">
                    {/* Status badge */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><BanknoteIcon className="size-6" /></div>
                        <div>
                          <h3 className="text-lg font-semibold text-foreground">{detail.request.currency_symbol || '$'}{detail.request.total_amount_needed}</h3>
                          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${(CIRCLE_STATUS_STYLES[detail.request.status] ?? { color: 'bg-muted text-muted-foreground' }).color}`}>
                            <ActivityIcon className="size-3" />{CIRCLE_STATUS_STYLES[detail.request.status]?.label ?? detail.request.status}
                          </span>
                        </div>
                      </div>
                      {detail.request.status !== 'cancelled' && detail.request.status !== 'completed' && (
                        <Button variant="destructive" size="sm" onClick={() => setConfirmCancel(true)} disabled={cancelling} className="gap-1.5">
                          {cancelling ? <LoaderIcon className="size-4 animate-spin" /> : <BanIcon className="size-4" />}Cancel
                        </Button>
                      )}
                    </div>

                    {/* Details */}
                    <div className="space-y-4">
                      <DetailField icon={<HashIcon className="size-4" />} label="Circle ID" value={`#${detail.request.circle_req_id}`} />
                      <DetailField icon={<TagIcon className="size-4" />} label="Circle name" value={detail.request.title ?? `${detail.request.creator_name}'s Circle`} />
                      <DetailField icon={<UsersIcon className="size-4" />} label="Creator" value={detail.request.creator_name} />
                      <DetailField icon={<BuildingIcon className="size-4" />} label="Organization" value={detail.request.org_name || '—'} />
                      <DetailField icon={<BanknoteIcon className="size-4" />} label="Per person" value={`${detail.request.currency_symbol || '$'}${detail.request.per_person_amount}`} />
                      <DetailField icon={<UsersIcon className="size-4" />} label="Participants" value={`${detail.request.participant_count} / ${detail.request.max_participants}`} />
                      <DetailField icon={<ClockIcon className="size-4" />} label="Created" value={formatTimestamp(detail.request.created_at)} />
                      {detail.request.full_at && <DetailField icon={<CheckCircleIcon className="size-4" />} label="Filled" value={formatTimestamp(detail.request.full_at)} />}
                      {detail.request.completed_at && <DetailField icon={<CheckCircleIcon className="size-4" />} label="Completed" value={formatTimestamp(detail.request.completed_at)} />}
                    </div>

                    {/* Participants */}
                    {detail.participants.length > 0 && (
                      <div className="border-t border-border pt-4">
                        <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">Participants</h4>
                        <div className="space-y-2">
                          {detail.participants.map((p) => (
                            <div key={p.req_part_id} className="flex items-center gap-3 rounded-lg bg-muted/50 px-3 py-2">
                              <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">{p.payout_order_position}</span>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-foreground">{p.name}</p>
                                <p className="text-xs text-muted-foreground">{p.email}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Payment Cycles */}
                    {detail.payment_cycles.length > 0 && (
                      <div className="border-t border-border pt-4">
                        <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">Payment cycles</h4>
                        <div className="space-y-2">
                          {detail.payment_cycles.map((cycle) => (
                            <div key={cycle.payment_cycle_id} className="rounded-lg bg-muted/50 px-3 py-2">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-foreground">Cycle #{cycle.cycle_number}</span>
                                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                  cycle.status === 'paid' ? 'bg-emerald-500/10 text-emerald-600' :
                                  cycle.status === 'overdue' ? 'bg-destructive/10 text-destructive' :
                                  'bg-amber-500/10 text-amber-600'
                                }`}>{cycle.status}</span>
                              </div>
                              <p className="mt-1 text-xs text-muted-foreground">
                                {detail.request.currency_symbol || '$'}{cycle.amount_per_person}/person · {detail.request.currency_symbol || '$'}{cycle.total_expected} total · Due {formatDate(cycle.due_date)}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="py-16 text-center text-sm text-muted-foreground">Could not load Circle details.</p>
                )}
              </div>
            </aside>

            {/* Cancel confirmation */}
            <DestructiveConfirm
              open={confirmCancel}
              onClose={() => setConfirmCancel(false)}
              title="Cancel this Circle?"
              description={`This will cancel the Circle for ${detail?.request.currency_symbol || '$'}${detail?.request.total_amount_needed} created by ${detail?.request.creator_name}. This action cannot be undone.`}
              confirmLabel="Yes, cancel Circle"
              onConfirm={handleCancel}
              loading={cancelling}
            />
          </>
        )}
      </div>
    )
  },
})

function DetailField({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">{icon}</div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">{label}</p>
        <p className="truncate text-sm font-medium text-foreground">{value}</p>
      </div>
    </div>
  )
}
